




















































































































































































































import {
  Component,
  Vue,
} from 'vue-property-decorator';
import {Action, Getter} from 'vuex-class';
import ConnectivityService from '@/services/connectivity.service';
import NetworkService from '@/services/network.service';
import {LoRaNetworkServer} from '@/types';
import Dialog from '@/components/Dialog.vue';
import CancelButton from '@/components/CancelButton.vue';
import ConfirmButton from '@/components/ConfirmButton.vue';
import GenericButton from '@/components/GenericButton.vue';
import LoRaWANServerForm from '@/components/LoRaWANServerForm.vue';
import NetmoreConfiguration from '@/views/catalogue/configuration/NetmoreConfiguration.vue';

export enum LNS {
  KERLINK = 'kerlink',
  THE_THINGS_INDUSTRY = 'theThingsIndustry',
  THE_THINGS_NETWORK = 'theThingsNetwork',
  IOT_SCOTLAND_OLD = 'boston-iot.net',
  IOT_SCOTLAND = 'north.loriot.io',
  ALLIOT_WANESY = 'alliot.wanesy.com',
  NETMORE = 'api.blink.services',
  LORIOT_MANAGED = 'loriot_managed',
  THINGPARK = 'actility-thingpark',
}

const compareText = (a: string, b: string) => (a || '').localeCompare(b || '');

@Component({
  components: {NetmoreConfiguration, GenericButton, ConfirmButton, CancelButton, Dialog, LoRaWANServerForm},
})
export default class LoRaWANSettings extends Vue {
  @Action private getGatewaySettingsAsync: any;
  @Action private addToastErrorMessage: any;
  @Getter private hasPermission!: any;

  private get canManage(): boolean {
    return this.hasPermission('network:manage');
  }

  private get sortedConfigs() {
    return [...this.configs].sort((a, b) =>
        (b.default ? 1 : 0) - (a.default ? 1 : 0) || compareText(this.displayName(a), this.displayName(b)));
  }

  private get takenHosts(): string[] {
    return this.configs.map((c) => c.lns);
  }

  private get canAdd(): boolean {
    return this.canManage && this.servers.some((s: any) => s.display === true && !this.takenHosts.includes(s.host));
  }

  private get renameUnchanged(): boolean {
    return !!this.configToRename && this.renameName.trim() === this.displayName(this.configToRename);
  }

  private configs: any[] = [];
  private servers: LoRaNetworkServer[] = [];
  private loading: boolean = true;
  private expandedIds: number[] = [];
  private adding: boolean = false;

  private snackbar: boolean = false;
  private snackbarText: string = '';

  private defaultingId: number | null = null;

  private showRenameDialog: boolean = false;
  private configToRename: any = null;
  private renameName: string = '';
  private renameValid: boolean = true;
  private renaming: boolean = false;
  private renameRules = [
    (v: string) => !!(v && v.trim()) || 'Name is required',
    (v: string) => !v || v.length <= 100 || 'Name must be 100 characters or fewer',
  ];

  private configToDelete: any = null;
  private removing: boolean = false;
  private removeDialog: boolean = false;
  private confirmRemoveDialog: boolean = false;
  private deleteConfirmText: string = '';

  private async mounted() {
    await this.load(true);
  }

  private async load(initial: boolean = false) {
    if (initial) {
      this.loading = true;
    }
    try {
      const [configs, servers] = await Promise.all([
        NetworkService.getOrganisationLnsList(),
        ConnectivityService.getLoRaNetworkServers(),
      ]);
      this.configs = configs || [];
      this.servers = servers || [];

      if (initial && this.sortedConfigs.length) {
        this.expandedIds = [this.sortedConfigs[0].id];
      }
    } catch (e) {
      this.addToastErrorMessage((e as Error).message);
    } finally {
      this.loading = false;
    }
  }

  private async refresh() {
    await this.load();
    this.getGatewaySettingsAsync();
  }

  private notify(text: string) {
    this.snackbarText = text;
    this.snackbar = true;
  }

  private serverTypeName(config: any): string {
    const server: any = this.servers.find((s: any) => s.host === config.lns);
    return server ? server.name : '';
  }

  private displayName(config: any): string {
    return config.name || this.serverTypeName(config) || 'Network server';
  }

  private cardTitle(config: any): string {
    const name = this.displayName(config);
    return name === 'default' ? (this.serverTypeName(config) || name) : name;
  }

  private canDelete(config: any): boolean {
    return !(config.default && this.configs.length > 1);
  }

  private isExpanded(config: any): boolean {
    return this.expandedIds.includes(config.id);
  }

  private toggleConfig(config: any) {
    this.expandedIds = this.isExpanded(config)
        ? this.expandedIds.filter((id) => id !== config.id)
        : [...this.expandedIds, config.id];
  }

  private addConfig() {
    this.adding = true;
  }

  private async onSaved(config?: any) {
    if (config) {
      this.expandedIds = this.expandedIds.filter((id) => id !== config.id);
    } else {
      this.adding = false;
    }
    this.notify('LoRa settings were saved.');
    await this.refresh();
  }

  private async setDefault(config: any) {
    if (config.default || this.defaultingId !== null) {
      return;
    }
    this.defaultingId = config.id;
    try {
      await ConnectivityService.updateLoRaNetworkServer(config.id, { default: true });
      this.notify(`${this.cardTitle(config)} is now the default network server.`);
      await this.refresh();
    } catch (e) {
      this.addToastErrorMessage((e as Error).message);
    } finally {
      this.defaultingId = null;
    }
  }

  private openRenameDialog(config: any) {
    this.configToRename = config;
    this.renameName = this.displayName(config);
    this.renameValid = true;
    this.showRenameDialog = true;
  }

  private closeRenameDialog() {
    this.showRenameDialog = false;
    this.configToRename = null;
  }

  private async saveRename() {
    const config = this.configToRename;
    const name = this.renameName.trim();
    if (!config || this.renaming || !this.renameValid || !name || this.renameUnchanged) {
      return;
    }
    this.renaming = true;
    try {
      await ConnectivityService.updateLoRaNetworkServer(config.id, { name });
      this.closeRenameDialog();
      await this.refresh();
    } catch (e) {
      this.addToastErrorMessage((e as Error).message);
    } finally {
      this.renaming = false;
    }
  }

  private requestDelete(config: any) {
    this.configToDelete = config;
    this.deleteConfirmText = '';
    this.removeDialog = true;
  }

  private confirmRemoval() {
    this.removeDialog = false;
    this.confirmRemoveDialog = true;
  }

  private closeDeleteDialogs() {
    if (this.removing) {
      return;
    }
    this.removeDialog = false;
    this.confirmRemoveDialog = false;
    this.configToDelete = null;
    this.deleteConfirmText = '';
  }

  private async deleteConfig() {
    const config = this.configToDelete;
    if (!config) {
      return;
    }
    this.removing = true;
    try {
      await ConnectivityService.removeLoRaSettings(config.id);
      this.expandedIds = this.expandedIds.filter((id) => id !== config.id);
      this.removing = false;
      this.closeDeleteDialogs();
      await this.refresh();
    } catch (e) {
      this.addToastErrorMessage((e as Error).message);
    } finally {
      this.removing = false;
    }
  }
}
